// League-themed color system for mobile-optimized design.
// Chapter IDs name the handicap the player ALREADY HAS — so `onboarding` is
// the "First Skills" stage where players begin with no handicap and earn HCP 54.
// Visuals shifted up one tier alongside the chapter rename so the same
// display name keeps its existing color identity.
export interface LeagueTheme {
  name: string;
  primary: {
    gradient: string;
    shadow: string;
    accent: string;
  };
  secondary: {
    background: string;
    border: string;
    text: string;
  };
  progress: {
    gradient: string;
    shadow: string;
  };
  icons: {
    background: string;
    shadow: string;
  };
}

export const leagueThemes: Record<string, LeagueTheme> = {
  onboarding: {
    name: 'First Skills',
    primary: {
      gradient: 'linear-gradient(135deg, #52B788 0%, #2D5A3D 100%)',
      shadow: '0 8px 32px rgba(82, 183, 136, 0.3), 0 4px 16px rgba(0, 0, 0, 0.1)',
      accent: '#52B788'
    },
    secondary: {
      background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)',
      border: 'rgba(82, 183, 136, 0.1)',
      text: '#166534'
    },
    progress: {
      gradient: 'linear-gradient(90deg, #52B788 0%, #2D5A3D 100%)',
      shadow: '0 1px 3px rgba(82, 183, 136, 0.4), inset 0 1px 1px rgba(255,255,255,0.3)'
    },
    icons: {
      background: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)',
      shadow: '0 2px 8px rgba(82, 183, 136, 0.15), inset 0 1px 1px rgba(255,255,255,0.8)'
    }
  },
  hcp_54: {
    name: 'Course Ready',
    primary: {
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
      shadow: '0 8px 32px rgba(59, 130, 246, 0.3), 0 4px 16px rgba(0, 0, 0, 0.1)',
      accent: '#3B82F6'
    },
    secondary: {
      background: 'linear-gradient(135deg, #FFFFFF 0%, #EFF6FF 100%)',
      border: 'rgba(59, 130, 246, 0.1)',
      text: '#1E40AF'
    },
    progress: {
      gradient: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)',
      shadow: '0 1px 3px rgba(59, 130, 246, 0.4), inset 0 1px 1px rgba(255,255,255,0.3)'
    },
    icons: {
      background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
      shadow: '0 2px 8px rgba(59, 130, 246, 0.15), inset 0 1px 1px rgba(255,255,255,0.8)'
    }
  },
  hcp_42: {
    name: 'Developing',
    primary: {
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #B45309 100%)',
      shadow: '0 8px 32px rgba(245, 158, 11, 0.3), 0 4px 16px rgba(0, 0, 0, 0.1)',
      accent: '#F59E0B'
    },
    secondary: {
      background: 'linear-gradient(135deg, #FFFFFF 0%, #FFFBEB 100%)',
      border: 'rgba(245, 158, 11, 0.1)',
      text: '#92400E'
    },
    progress: {
      gradient: 'linear-gradient(90deg, #F59E0B 0%, #B45309 100%)',
      shadow: '0 1px 3px rgba(245, 158, 11, 0.4), inset 0 1px 1px rgba(255,255,255,0.3)'
    },
    icons: {
      background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
      shadow: '0 2px 8px rgba(245, 158, 11, 0.2), inset 0 1px 1px rgba(255,255,255,0.8)'
    }
  },
  hcp_36: {
    name: 'Advancing',
    primary: {
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
      shadow: '0 8px 32px rgba(139, 92, 246, 0.3), 0 4px 16px rgba(0, 0, 0, 0.1)',
      accent: '#8B5CF6'
    },
    secondary: {
      background: 'linear-gradient(135deg, #FFFFFF 0%, #FAF5FF 100%)',
      border: 'rgba(139, 92, 246, 0.1)',
      text: '#6B21A8'
    },
    progress: {
      gradient: 'linear-gradient(90deg, #8B5CF6 0%, #6D28D9 100%)',
      shadow: '0 1px 3px rgba(139, 92, 246, 0.4), inset 0 1px 1px rgba(255,255,255,0.3)'
    },
    icons: {
      background: 'linear-gradient(135deg, #F3E8FF 0%, #E9D5FF 100%)',
      shadow: '0 2px 8px rgba(139, 92, 246, 0.15), inset 0 1px 1px rgba(255,255,255,0.8)'
    }
  },
  hcp_28: {
    name: 'Skilled',
    primary: {
      gradient: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)',
      shadow: '0 8px 32px rgba(239, 68, 68, 0.3), 0 4px 16px rgba(0, 0, 0, 0.1)',
      accent: '#EF4444'
    },
    secondary: {
      background: 'linear-gradient(135deg, #FFFFFF 0%, #FEF2F2 100%)',
      border: 'rgba(239, 68, 68, 0.1)',
      text: '#991B1B'
    },
    progress: {
      gradient: 'linear-gradient(90deg, #EF4444 0%, #B91C1C 100%)',
      shadow: '0 1px 3px rgba(239, 68, 68, 0.4), inset 0 1px 1px rgba(255,255,255,0.3)'
    },
    icons: {
      background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
      shadow: '0 2px 8px rgba(239, 68, 68, 0.15), inset 0 1px 1px rgba(255,255,255,0.8)'
    }
  },
  hcp_18: {
    name: 'Elite',
    primary: {
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #92400E 100%)',
      shadow: '0 8px 32px rgba(245, 158, 11, 0.4), 0 4px 16px rgba(0, 0, 0, 0.1)',
      accent: '#F59E0B'
    },
    secondary: {
      background: 'linear-gradient(135deg, #FFFFFF 0%, #FFFBEB 100%)',
      border: 'rgba(245, 158, 11, 0.1)',
      text: '#78350F'
    },
    progress: {
      gradient: 'linear-gradient(90deg, #F59E0B 0%, #92400E 100%)',
      shadow: '0 1px 3px rgba(245, 158, 11, 0.4), inset 0 1px 1px rgba(255,255,255,0.3)'
    },
    icons: {
      background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
      shadow: '0 2px 8px rgba(245, 158, 11, 0.2), inset 0 1px 1px rgba(255,255,255,0.8)'
    }
  },
  hcp_10: {
    name: 'Master',
    primary: {
      gradient: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
      shadow: '0 8px 32px rgba(124, 58, 237, 0.4), 0 4px 16px rgba(0, 0, 0, 0.1)',
      accent: '#7C3AED'
    },
    secondary: {
      background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F3FF 100%)',
      border: 'rgba(124, 58, 237, 0.1)',
      text: '#4C1D95'
    },
    progress: {
      gradient: 'linear-gradient(90deg, #7C3AED 0%, #5B21B6 100%)',
      shadow: '0 1px 3px rgba(124, 58, 237, 0.4), inset 0 1px 1px rgba(255,255,255,0.3)'
    },
    icons: {
      background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
      shadow: '0 2px 8px rgba(124, 58, 237, 0.2), inset 0 1px 1px rgba(255,255,255,0.8)'
    }
  },
};

export function getLeagueTheme(league: string): LeagueTheme {
  return leagueThemes[league.toLowerCase()] || leagueThemes.onboarding;
}

export function getLeagueTextColors(league: string) {
  const theme = getLeagueTheme(league);
  return {
    primary: theme.secondary.text,
    accent: theme.primary.accent,
    muted: `${theme.primary.accent}80` // 50% opacity
  };
}
